import * as React from "react"
import { HTMLInputTypeAttribute } from "react"
import styled from "styled-components"
import cn from "classnames"
import * as CSS from "csstype"

import { IconTypes, IconAlignTypes } from "../types"
import { mixins, themeValues } from "../theme"
import { ButtonKinds, ButtonSizes, ButtonThemes, ButtonStates } from "./types"
import { Icon } from "../Icon/Icon"

const StyledButton = styled.button`
    ${mixins.rounded};
    ${mixins.centerFlex};
    flex-shrink: 0;

    color: white;
    cursor: pointer;
    transition: background-color 0.2s ease;
    font-weight: 500;
    border: 1px solid transparent;
    outline: 0;
    padding: 4px 8px;
    position: relative;

    .loader {
        display: none;
    }

    i {
        &.marginRight {
            margin-right: 8px;
        }
        &.marginLeft {
            margin-left: 8px;
        }
    }

    &:not(.iconOnly) {
        .content {
            &.has-icon {
                width: 100%;
            }

            .children {
                display: flex;
                align-items: center;
                justify-content: space-between;
                white-space: nowrap;
            }
        }
    }

    &.loading {
        .content {
            .children {
                opacity: 0;
            }
            .loader {
                display: flex;
                align-items: center;
                justify-content: center;
                top: 0;
                left: 0;
                height: 100%;
                width: 100%;
            }
        }
    }

    &.danger {
        &.fill {
            background-color: ${themeValues.colors.red100};
            color: ${themeValues.colors.white};
        }

        &.outline {
            border: 1px solid;
            border-color: ${themeValues.colors.red100};
            background-color: transparent;
            color: ${themeValues.colors.red100};
        }

        &.ghost {
            background-color: transparent;
            color: ${themeValues.colors.red100};
        }
    }

    &.primary-revert {
        background-color: ${themeValues.colors.blue24};
        color: ${themeValues.colors.blue100};
        &.outline {
            border: 1px solid;
            border-color: ${themeValues.colors.blue24};
            background-color: transparent;
            color: ${themeValues.colors.blue100};
        }
        &.ghost {
            background-color: transparent;
            color: ${themeValues.colors.blue100};
        }
    }

    &.primary {
        &.fill {
            background-color: ${themeValues.colors.blue100};
            color: ${themeValues.colors.white};
        }

        &.outline {
            border: 1px solid;
            border-color: ${themeValues.colors.blue100};
            background-color: transparent;
            color: ${themeValues.colors.blue100};
        }

        &.ghost {
            background-color: transparent;
            color: ${themeValues.colors.blue100};
        }
    }

    &.secondary {
        color: ${themeValues.colors.dark100};

        &.fill {
            background-color: ${themeValues.colors.dark16};
        }

        &.outline {
            border: 1px solid;
            border-color: ${themeValues.colors.dark32};
            background-color: transparent;
        }

        &.ghost {
            background-color: transparent;
        }
    }

    &.small {
        height: ${themeValues.inputSize.small};
        min-width: ${themeValues.inputSize.small};
        font-size: ${themeValues.fontSize.normal};
        &.minWidth {
            min-width: 64px;
        }
    }

    &.medium {
        height: ${themeValues.inputSize.medium};
        min-width: ${themeValues.inputSize.medium};
        font-size: ${themeValues.fontSize.large};
        padding: 8px 16px;

        &.minWidth {
            min-width: 96px;
        }
    }

    &.large {
        height: ${themeValues.inputSize.large};
        min-width: ${themeValues.inputSize.large};
        font-size: ${themeValues.fontSize.large};
        padding: 8px 16px;

        &.minWidth {
            min-width: 128px;
        }
    }

    &[disabled] {
        cursor: not-allowed;

        &.primary,
        &.primary-revert {
            color: ${themeValues.colors.dark48};
            &.fill {
                background-color: ${themeValues.colors.dark16};
            }
            &.outline {
                border-color: ${themeValues.colors.dark16};
            }
            &.ghost {
            }
        }
        &.secondary,
        &.danger {
            color: ${themeValues.colors.dark48};
            &.fill {
                background-color: ${themeValues.colors.dark16};
            }
            &.outline {
                border-color: ${themeValues.colors.dark16};
            }
            &.ghost {
            }
        }
    }

    &:not([disabled]) {
        &:hover,
        &.hover {
            &.primary {
                &.fill {
                    background-color: ${themeValues.colors.blue48};
                }
                &.outline,
                &.ghost {
                    background-color: ${themeValues.colors.blue24};
                }
            }
            &.secondary {
                &.fill {
                    background-color: ${themeValues.colors.dark32};
                }
                &.outline,
                &.ghost {
                    background-color: ${themeValues.colors.dark16};
                }
            }
            &.primary-revert {
                &.fill {
                    background-color: white;
                    border-color: ${themeValues.colors.blue24};
                }
                &.outline,
                &.ghost {
                    background-color: ${themeValues.colors.blue24};
                }
            }
            &.danger {
                &.fill {
                    background-color: ${themeValues.colors.red48};
                }
                &.outline,
                &.ghost {
                    background-color: ${themeValues.colors.red48};
                }
            }
        }
        &:active,
        &.active {
            &.primary {
                &.fill {
                    background-color: ${themeValues.colors.blueDark};
                }
                &.outline,
                &.ghost {
                    background-color: ${themeValues.colors.blue24};
                }
            }
            &.secondary {
                &.fill {
                    background-color: ${themeValues.colors.dark48};
                }
                &.outline,
                &.ghost {
                    background-color: ${themeValues.colors.dark32};
                }
            }
            &.primary-revert {
                &.fill {
                    background-color: ${themeValues.colors.blue48};
                }
                &.outline,
                &.ghost {
                    background-color: ${themeValues.colors.blue24};
                }
            }
            &.danger {
                &.fill {
                    background-color: ${themeValues.colors.redDark};
                }
                &.outline,
                &.ghost {
                    background-color: ${themeValues.colors.redDark};
                }
            }
        }
    }

    &.fullwidth {
        width: 100%;
    }

    &.iconOnly {
        padding: 0;
    }
`
StyledButton.displayName = "StyledButton"

export interface IButtonProps {
    size?: ButtonSizes
    kind?: ButtonKinds
    theme?: ButtonThemes
    state?: ButtonStates
    children?: JSX.Element | string
    disabled?: boolean
    loading?: boolean
    loadingLabel?: string
    onClick?: () => void
    icon?: IconTypes | JSX.Element
    iconAlign?: IconAlignTypes
    fullWidth?: boolean
    className?: string
    style?: CSS.Properties
    id?: string
    ref?: React.RefObject<HTMLButtonElement>
}

export function Button({
    children,
    onClick,
    disabled,
    loadingLabel,
    loading,
    fullWidth,
    state,
    icon,
    iconAlign = "left",
    size = "medium",
    kind: btnKind = "primary",
    theme: btnTheme = "fill",
    style,
    className,
    id,
    ref,
}: IButtonProps): JSX.Element {
    let kind = btnKind || "primary"
    let theme = btnTheme || "fill"
    let loadingText = loadingLabel || "Loading..."
    if (state == "loading") loading = true
    const iconOnly = !children
    const hasIcon = !!icon

    const renderIcon = (align: string): JSX.Element | null => {
        if (icon) {
            if (React.isValidElement(icon)) {
                return icon
            } else {
                return (
                    <Icon
                        size={size == "small" ? "xsmall" : "small"}
                        glyph={icon as IconTypes}
                        className={cn({
                            marginLeft: align == "right" && !!children,
                            marginRight: align == "left" && !!children,
                            disabled: disabled,
                        })}
                    />
                )
            }
        }
        return null
    }

    return (
        <StyledButton
            id={id}
            ref={ref}
            onClick={onClick}
            disabled={disabled || loading}
            style={style}
            className={cn(
                kind,
                theme,
                size,
                state,
                {
                    fullwidth: fullWidth,
                    loading: loading,
                    iconOnly: iconOnly,
                },
                className,
            )}
        >
            <div className={cn("content", loading, { "has-icon": hasIcon })}>
                {loading ? (
                    <span className="loader">{loadingText}</span>
                ) : (
                    <span className="children">
                        {icon && iconAlign == "left" && renderIcon(iconAlign)}
                        {children}
                        {icon && iconAlign == "right" && renderIcon(iconAlign)}
                    </span>
                )}
            </div>
        </StyledButton>
    )
}
